import React, { useState, useEffect } from "react";
import { Table, Row, Col, Collapse, Button } from "reactstrap";
import { toast } from "react-toastify";
// componentes
import Pagination from "../../../../components/Paginationv3";
import SectionComponent from "../../../Apps/Personalization/SectionComponent";
import LoadingSkelentonTable from "../../../../components/loadingSkeletonTable";
import WaitExport from "../../../../components/WaitExport";
// service
import { getOnline } from "../../../../utils/services/dashboardSuperScreen";
// utils
import useDebounce from "../../../../utils/hooks/useDebounce";
import { createXlsx, formatDate } from "./exports/createXlsxOnline";

export default function TableOnline(props) {
  const eventId = props.idEvent;
  const transmissionId = props.idDashboard;
  // state de pesquisa
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  // state de paginação
  const [currentIndex, setCurrentIndex] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  // força um novo fetch mesmo quando nenhum filtro mudou (botão Refresh)
  const [refreshKey, setRefreshKey] = useState(0);
  // state do collapse de opções (mobile)
  const [isOpen, setIsOpen] = useState(false);
  // state de exportação
  const [exportInProgress, setExportInProgress] = useState(false);
  const [finishExport, setFinishExport] = useState(false);

  const online = props.tableData.online;
  const countData = online.total ?? 0;

  // atualiza os dados de participantes online no state compartilhado do dashboard
  function updateOnline(values) {
    props.setTableData((state) => ({
      ...state,
      online: { ...state.online, ...values },
    }));
  }

  // inicia a exportação dos participantes online em xlsx
  function exportReport() {
    createXlsx({
      eventId,
      transmissionID: transmissionId,
      search: "",
      countData,
      mobileapps: props.mobileapps,
      setFinishExport,
      setExportInProgress,
    });
    setExportInProgress(true);
  }

  // atualiza o termo pesquisado e volta para a primeira página, senão a busca
  // sairia com o offset da página em que o usuário estava
  function handleSearch(value) {
    setSearch(value.toLowerCase());
    setCurrentIndex(1);
  }

  // volta a listagem para o estado inicial. O incremento do refreshKey dispara o
  // refetch pelo useEffect, já com o state atualizado, em vez de chamar o fetch
  // aqui com os valores antigos do render
  function resetPagination() {
    setSearch("");
    setItemsPerPage(10);
    setCurrentIndex(1);
    setRefreshKey((key) => key + 1);
  }

  useEffect(() => {
    // enquanto o debounce não fecha, search e debouncedSearch divergem e o fetch
    // sairia com o termo antigo. O próprio debounce dispara este efeito de novo
    if (search !== debouncedSearch) return;
    if (!props.mobileapps) return;

    // ignora respostas de requisições antigas (página ou pesquisa anterior)
    let ignore = false;

    updateOnline({ loading: true });
    getOnline({
      eventId,
      transmissionID: transmissionId,
      pageSize: itemsPerPage,
      offset: currentIndex - 1,
      search: debouncedSearch,
      mobileapps: props.mobileapps,
    })
      .then((res) => {
        if (ignore) return;

        if (res?.success) {
          updateOnline({ loading: false, data: res.data, total: res.total });
        } else {
          updateOnline({ loading: false });
          toast.error("Erro ao buscar participantes online.");
        }
      })
      .catch(() => {
        if (ignore) return;

        updateOnline({ loading: false });
        toast.error("Erro ao buscar participantes online.");
      });

    return () => {
      ignore = true;
    };
  }, [
    debouncedSearch,
    itemsPerPage,
    currentIndex,
    props.mobileapps,
    refreshKey,
  ]);

  return (
    <div>
      <WaitExport
        isOpen={exportInProgress}
        name="Relatório"
        preposicao="dos"
        finishExport={finishExport}
        setFinishExport={setFinishExport}
        setExportInProgress={setExportInProgress}
      />
      <div
        style={{
          display: "flex",
          width: "100%",
          justifyContent: "space-between",
          margin: "10px 0",
        }}
      >
        <form
          className="app-search d-none d-lg-block events-input-search"
          onSubmit={(e) => e.preventDefault()}
        >
          <div className="position-relative">
            <input
              type="text"
              className="form-control"
              placeholder="Pesquisar..."
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
            />
            <span className="bx bx-search-alt" />
          </div>
        </form>

        <div className="mobile-btn-section" style={{ width: "100%" }}>
          <Row className="mobile-btns-top">
            <form
              className="app-search"
              style={{ padding: "0px" }}
              onSubmit={(e) => e.preventDefault()}
            >
              <div className="position-relative">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Pesquisar..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                />
                <span className="bx bx-search-alt" />
              </div>
            </form>
          </Row>
          {online.data.length > 0 && (
            <>
              <SectionComponent
                sectionName="Opções"
                setIsOpen={setIsOpen}
                isOpen={isOpen}
              />
              <Collapse isOpen={isOpen}>
                <Row className="mobile-btns-top">
                  <div onClick={() => exportReport()}>
                    Exportar Participantes
                  </div>
                </Row>
              </Collapse>
            </>
          )}
        </div>

        <div className="header-btns-container">
          {online.data.length > 0 && (
            <Button
              color="success"
              className="btn-rounded waves-effect waves-light events-button-add"
              onClick={() => exportReport()}
            >
              Exportar Participantes
            </Button>
          )}
        </div>
      </div>

      <div
        style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}
      >
        <button
          type="button"
          className="btn-reset-table border-0"
          onClick={() => resetPagination()}
        >
          <i className="mdi mdi-refresh" style={{ marginRight: 5 }} />
          Refresh
        </button>
      </div>

      <div className="table-responsive bg-white">
        <Table className="table mb-0">
          <thead className="table-light">
            <tr>
              <th>ID do Participante</th>
              <th>Nome</th>
              <th>Data e hora</th>
            </tr>
          </thead>

          <tbody>
            {online.loading ? (
              <LoadingSkelentonTable lines={3} cols={3} />
            ) : (
              online.data.map((e) => (
                <tr key={e.id}>
                  <td>{e.id}</td>
                  <td>{e.name}</td>
                  <td>{formatDate(e.date)}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
      <Row style={{ marginTop: 20 }}>
        <Col md={12}>
          <Pagination
            data={online.data}
            search={search}
            currentIndex={currentIndex}
            setCurrentIndex={setCurrentIndex}
            countData={countData}
            maxItemPerPage={itemsPerPage}
            setMaxItemPerPage={setItemsPerPage}
            maxPageButtonsToShow={3}
          />
        </Col>
      </Row>
    </div>
  );
}
